import type { ErrorLike } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  Alert,
  Anchor,
  Breadcrumbs,
  Button,
  Card,
  Code,
  Group,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconRefresh, IconSparkles } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { toApiError } from '../apollo/errors';
import { QueryState } from '../components/QueryState';
import { StatusBadge } from '../components/StatusBadge';
import {
  AdminGenerateStoryDocument,
  AdminRetryStoryDocument,
  AdminStoryDocument,
} from '../graphql/operations';
import { shortId, useLabels } from '../labels';

export function StoryDetailPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const labels = useLabels();
  const { data, loading, error } = useQuery(AdminStoryDocument, { variables: { id } });
  const [retry, { loading: retrying }] = useMutation(AdminRetryStoryDocument);
  // No refetch: the answer carries the same id, so Apollo merges it into the
  // cached story and this page re-renders with the new text.
  const [generate, { loading: generating }] = useMutation(AdminGenerateStoryDocument);

  async function retryStory() {
    try {
      await retry({ variables: { id } });
      notifications.show({ color: 'teal', message: t('stories.detail.retried') });
    } catch (thrown) {
      notifications.show({
        color: 'red',
        title: t('stories.detail.retryFailed'),
        message: toApiError(thrown as Error)?.message ?? '',
      });
    }
  }

  async function generateStory() {
    try {
      await generate({ variables: { id } });
      notifications.show({ color: 'teal', message: t('stories.detail.generated') });
    } catch (thrown) {
      // Nothing here is fixable from this screen -- a refused model, a rate
      // limit, a key the API's environment does not have -- so the reason the
      // API gave is the whole of the answer.
      const failure = toApiError(thrown as ErrorLike);

      notifications.show({
        color: 'red',
        title: t('stories.detail.generateFailed'),
        message: failure?.message ?? '',
      });
    }
  }

  /**
   * A story that already has text is being rewritten, not written, and the old
   * text does not come back -- so that one gets a question first.
   */
  function askThenGenerate(text: string | null | undefined) {
    if (!text) {
      void generateStory();

      return;
    }

    modals.openConfirmModal({
      title: t('stories.detail.generateConfirmTitle'),
      centered: true,
      children: <Text size="sm">{t('stories.detail.generateConfirm')}</Text>,
      labels: { confirm: t('common.confirm'), cancel: t('common.cancel') },
      onConfirm: () => void generateStory(),
    });
  }

  const story = data?.adminStory;

  return (
    <Stack gap="md">
      <Breadcrumbs>
        <Anchor component={Link} to="/stories">
          {t('stories.title')}
        </Anchor>
        <Text>{shortId(id)}</Text>
      </Breadcrumbs>

      <QueryState loading={loading && !data} error={error}>
        {story && (
          <>
            <Group justify="space-between" align="start">
              <Group gap="sm">
                <Title order={2}>{story.title ?? t('stories.detail.untitled')}</Title>
                <StatusBadge status={story.status} />
              </Group>
              <Group gap="sm">
                {/* The API refuses a story that is already on its way, so the
                    button appears when it applies rather than always being
                    there and usually erroring. */}
                {story.status !== 'queued' && story.status !== 'generating' && (
                  <Button
                    variant="light"
                    leftSection={<IconSparkles size={16} />}
                    loading={generating}
                    onClick={() => {
                      askThenGenerate(story.text);
                    }}
                  >
                    {t('stories.detail.generate')}
                  </Button>
                )}
                {/* Only a failed story can be re-queued, for the same reason. */}
                {story.status === 'failed' && (
                  <Button
                    leftSection={<IconRefresh size={16} />}
                    loading={retrying}
                    onClick={() => void retryStory()}
                  >
                    {t('stories.detail.retry')}
                  </Button>
                )}
              </Group>
            </Group>

            {/* The call is synchronous and slow; without this the page looks
                stuck rather than busy. */}
            {generating && (
              <Text c="dimmed" size="sm">
                {t('stories.detail.generating')}
              </Text>
            )}

            {story.error && (
              <Alert
                color="red"
                icon={<IconAlertTriangle size={18} />}
                title={t('stories.detail.generationError')}
              >
                <Stack gap="xs">
                  <Text size="sm">{story.error}</Text>
                  {/* The app's wording is written for a parent and says nothing
                      about why. This is the reason upstream actually gave, and
                      it is the whole point of opening this page. */}
                  {story.errorDetail && (
                    <div>
                      <Text size="xs" c="dimmed">
                        {t('stories.detail.generationDetail')}
                      </Text>
                      <Code block>{story.errorDetail}</Code>
                    </div>
                  )}
                </Stack>
              </Alert>
            )}

            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
              <Card withBorder padding="md">
                <Stack gap="xs">
                  <Field label={t('common.id')} value={story.id} />
                  <Field label={t('stories.detail.mode')} value={labels.storyMode(story.mode)} />
                  <Field label={t('stories.detail.locale')} value={labels.locale(story.locale)} />
                  <Field
                    label={t('stories.detail.createdAt')}
                    value={labels.dateTime(story.createdAt)}
                  />
                  <Field
                    label={t('stories.detail.updatedAt')}
                    value={labels.dateTime(story.updatedAt)}
                  />
                </Stack>
              </Card>

              <Card withBorder padding="md">
                <Stack gap="xs">
                  <Text fw={600}>{t('stories.detail.user')}</Text>
                  <Anchor component={Link} to={`/users/${story.user.id}`} size="sm">
                    {story.user.email ?? shortId(story.user.id)}
                  </Anchor>
                  <Field label={t('common.id')} value={story.user.id} />
                  <Field label={t('stories.detail.plan')} value={labels.plan(story.user.plan)} />
                </Stack>
              </Card>
            </SimpleGrid>

            {story.text && (
              <Card withBorder padding="md">
                <Stack gap="xs">
                  <Text fw={600}>{t('stories.detail.text')}</Text>
                  <Text style={{ whiteSpace: 'pre-wrap' }}>{story.text}</Text>
                </Stack>
              </Card>
            )}

            <Stack gap="xs">
              <Text fw={600}>{t('stories.detail.draft')}</Text>
              <Code block>{JSON.stringify(story.draft, null, 2)}</Code>
            </Stack>
          </>
        )}
      </QueryState>
    </Stack>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <Group justify="space-between" gap="xl" wrap="nowrap">
      <Text size="sm" c="dimmed">
        {label}
      </Text>
      <Text size="sm" style={{ wordBreak: 'break-all' }} ta="right">
        {value}
      </Text>
    </Group>
  );
}
